import { CSSProperties } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import styles from './PageHero.module.scss';

/**
 * Which page's accent tints the overlay. Each page keeps the color it already
 * uses throughout the rest of its sections -- green on About, orange on
 * Insights, purple on Events, navy on Services and Careers -- so the hero
 * introduces the page rather than overriding it.
 */
export type HeroTone =
  | 'services'
  | 'insights'
  | 'events'
  | 'about'
  | 'careers'
  // The 404, which belongs to no practice and takes the site's own navy.
  | 'notFound';

interface PageHeroProps {
  title: string;
  subtitle?: string;
  /**
   * An imported image, never a string path. Every page hero that hardcoded a
   * root-absolute `/assets/...` URL was silently 404ing -- see the note below.
   */
  image: string;
  tone: HeroTone;
  /**
   * Renders the "Scroll" cue. Pages that had one keep it; pages that never had
   * one do not gain it.
   */
  showScrollHint?: boolean;
}

/**
 * The one page hero: background image, colored overlay, title, subtitle.
 *
 * Replaces five hand-built heroes that shared a shape but agreed on nothing
 * else -- five sets of responsive rules, two layouts, and three different ways
 * of loading the image. `SITE_AUDIT.md` §7 counted seven near-identical hero
 * implementations across the site; this collapses five of them.
 *
 * What is deliberately NOT unified is the color. Each page's overlay is tinted
 * with the accent that page already uses everywhere else, so the heroes share
 * a structure without flattening the site into one palette.
 *
 * IMAGES ARE IMPORTED, NOT REFERENCED BY PATH. The Events hero used
 * `url('/assets/banne25 (1).webp')` and the Careers hero used
 * `url('/assets/grafics.jpg')`, both root-absolute. There is no
 * `public/assets/` directory in this repo, so both requests 404 in production
 * and those heroes have been rendering as a bare gradient. Importing the
 * asset makes the bundler resolve and fingerprint it, which is also what
 * makes the 404 impossible to reintroduce silently.
 */
const PageHero = ({
  title,
  subtitle,
  image,
  tone,
  showScrollHint = false,
}: PageHeroProps) => {
  const handleScrollHintClick = () => {
    window.scrollTo({ top: window.innerHeight - 80, behavior: 'smooth' });
  };

  return (
    <section
      className={`${styles.pageHero} ${styles[tone]}`}
      // The quotes around the URL are load-bearing. Three of the five hero
      // assets are named with a space and a "(1)" suffix -- the bundler keeps
      // those characters in the emitted filename ("banne25 (1).<hash>.webp").
      // An unquoted CSS url() token cannot contain whitespace or unescaped
      // parentheses, so the whole `background` shorthand that consumes this
      // custom property is invalid and dropped, and the hero renders as a flat
      // gradient with no photograph. The stylesheets this replaced quoted their
      // url() calls, which is why they did not hit this.
      style={{ '--hero-image': `url("${image}")` } as CSSProperties}
    >
      <div className={styles.container}>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>

      {showScrollHint && (
        <button
          type="button"
          className={styles.scrollHint}
          onClick={handleScrollHintClick}
          aria-label="Scroll down"
        >
          <FaChevronDown aria-hidden="true" />
          <span>Scroll</span>
        </button>
      )}
    </section>
  );
};

export default PageHero;
